// This file is auto-generated by @hey-api/openapi-ts

import { createClient, createConfig, type OptionsLegacyParser } from '@hey-api/client-fetch';
import type { AuthInitiateData, GetAuthStatusError, GetAuthStatusResponse, LogoutError, LogoutResponse, GetMeError, GetMeResponse, CreateWsTicketError, CreateWsTicketResponse, SendChatMessageData, SendChatMessageError, SendChatMessageResponse, ListSessionsData, ListSessionsError, ListSessionsResponse, SearchSessionsData, SearchSessionsError, SearchSessionsResponse, GetSessionData, GetSessionError, GetSessionResponse, DeleteSessionData, DeleteSessionError, DeleteSessionResponse, ArchiveSessionData, ArchiveSessionError, ArchiveSessionResponse, GetUserPreferencesError, GetUserPreferencesResponse, PutUserPreferencesData, PutUserPreferencesError, PutUserPreferencesResponse, ListConsentsError, ListConsentsResponse, RevokeConsentData, RevokeConsentError, RevokeConsentResponse, ListNotificationsData, ListNotificationsError, ListNotificationsResponse, MarkNotificationReadData, MarkNotificationReadError, MarkNotificationReadResponse, GetUserGraphData, GetUserGraphError, GetUserGraphResponse, GetDiscoveryStatusError, GetDiscoveryStatusResponse, ListConnectionsError, ListConnectionsResponse, ListMemoryData, ListMemoryError, ListMemoryResponse, SlackOauthCallbackData, SlackOauthCallbackError, SlackOauthCallbackResponse, ListCustomAgentsError, ListCustomAgentsResponse, CreateCustomAgentData, CreateCustomAgentError, CreateCustomAgentResponse, GetCustomAgentData, GetCustomAgentError, GetCustomAgentResponse, UpdateCustomAgentData, UpdateCustomAgentError, UpdateCustomAgentResponse, DeleteCustomAgentData, DeleteCustomAgentError, DeleteCustomAgentResponse, ListWorkflowsError, ListWorkflowsResponse, CreateWorkflowData, CreateWorkflowError, CreateWorkflowResponse, GetWorkflowData, GetWorkflowError, GetWorkflowResponse, UpdateWorkflowData, UpdateWorkflowError, UpdateWorkflowResponse, DeleteWorkflowData, DeleteWorkflowError, DeleteWorkflowResponse, DryRunWorkflowData, DryRunWorkflowError, DryRunWorkflowResponse, RunWorkflowData, RunWorkflowError, RunWorkflowResponse } from './types.gen';

export const client = createClient(createConfig());

/**
 * Begin Midway login
 * Server starts the OAuth flow and 302s to Midway. The frontend never
 * XHRs this endpoint — it `window.location.assign`s to it on a 401.
 * Documented here only so the route is on the contract.
 *
 */
export const authInitiate = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<AuthInitiateData, ThrowOnError>) => {
    return (options?.client ?? client).post<void, unknown, ThrowOnError>({
        ...options,
        url: '/auth/initiate'
    });
};

/**
 * Check session status
 * Lightweight probe. Returns `{ authenticated: false }` for anon (does
 * NOT 401) so the SPA's auth-context boot doesn't loop. The frontend
 * treats `authenticated: true` as the trigger to fetch `/me`.
 *
 */
export const getAuthStatus = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).get<GetAuthStatusResponse, GetAuthStatusError, ThrowOnError>({
        ...options,
        url: '/auth/status'
    });
};

/**
 * Invalidate the current session
 * Clears HttpOnly cookies server-side. CSRF-protected.
 */
export const logout = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).post<LogoutResponse, LogoutError, ThrowOnError>({
        ...options,
        url: '/auth/logout'
    });
};

/**
 * Get the authenticated user's identity
 * Hydrates `AuthContext`. Returns the employee's identity, manager,
 * ldap groups, and whether they have completed first-run onboarding.
 * **Never** returns a token.
 *
 */
export const getMe = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).get<GetMeResponse, GetMeError, ThrowOnError>({
        ...options,
        url: '/me'
    });
};

/**
 * Mint a short-lived WebSocket signed URL
 * Returns a fully-qualified `wss://…?ticket=…` URL the SPA can hand to
 * partysocket. The ticket is signed (HMAC) by the API and embeds the
 * employeeId, sessionId scope, and an `exp` claim. Tickets are
 * single-use within a 60-second window.
 *
 */
export const createWsTicket = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).post<CreateWsTicketResponse, CreateWsTicketError, ThrowOnError>({
        ...options,
        url: '/ws/ticket'
    });
};

/**
 * Send a chat message (non-streaming fallback)
 * Used when the SPA cannot open a WebSocket (e.g. corp-network blocks
 * WebSocket upgrades). Streaming chat ALWAYS prefers the WS envelope
 * (`agent.token` events). The response here is a fully-realized
 * message (no tokens).
 *
 */
export const sendChatMessage = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<SendChatMessageData, ThrowOnError>) => {
    return (options?.client ?? client).post<SendChatMessageResponse, SendChatMessageError, ThrowOnError>({
        ...options,
        url: '/chat/message'
    });
};

/**
 * List the caller's recent conversations
 */
export const listSessions = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<ListSessionsData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListSessionsResponse, ListSessionsError, ThrowOnError>({
        ...options,
        url: '/sessions/list'
    });
};

/**
 * Search the caller's conversations
 */
export const searchSessions = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<SearchSessionsData, ThrowOnError>) => {
    return (options?.client ?? client).get<SearchSessionsResponse, SearchSessionsError, ThrowOnError>({
        ...options,
        url: '/sessions/search'
    });
};

/**
 * Fetch one conversation with all its messages
 */
export const getSession = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<GetSessionData, ThrowOnError>) => {
    return (options?.client ?? client).get<GetSessionResponse, GetSessionError, ThrowOnError>({
        ...options,
        url: '/sessions/{sessionId}'
    });
};

/**
 * Hard-delete a conversation
 */
export const deleteSession = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<DeleteSessionData, ThrowOnError>) => {
    return (options?.client ?? client).delete<DeleteSessionResponse, DeleteSessionError, ThrowOnError>({
        ...options,
        url: '/sessions/{sessionId}'
    });
};

/**
 * Archive (soft-delete) a conversation
 */
export const archiveSession = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<ArchiveSessionData, ThrowOnError>) => {
    return (options?.client ?? client).post<ArchiveSessionResponse, ArchiveSessionError, ThrowOnError>({
        ...options,
        url: '/sessions/{sessionId}/archive'
    });
};

/**
 * Get the caller's preferences
 */
export const getUserPreferences = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).get<GetUserPreferencesResponse, GetUserPreferencesError, ThrowOnError>({
        ...options,
        url: '/users/preferences'
    });
};

/**
 * Replace the caller's preferences
 */
export const putUserPreferences = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<PutUserPreferencesData, ThrowOnError>) => {
    return (options?.client ?? client).put<PutUserPreferencesResponse, PutUserPreferencesError, ThrowOnError>({
        ...options,
        url: '/users/preferences'
    });
};

/**
 * List the caller's active OBO scope grants
 */
export const listConsents = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).get<ListConsentsResponse, ListConsentsError, ThrowOnError>({
        ...options,
        url: '/users/consents'
    });
};

/**
 * Revoke a single scope grant
 */
export const revokeConsent = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<RevokeConsentData, ThrowOnError>) => {
    return (options?.client ?? client).delete<RevokeConsentResponse, RevokeConsentError, ThrowOnError>({
        ...options,
        url: '/users/consents/{consentId}'
    });
};

/**
 * List in-app notifications
 */
export const listNotifications = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<ListNotificationsData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListNotificationsResponse, ListNotificationsError, ThrowOnError>({
        ...options,
        url: '/users/notifications'
    });
};

/**
 * Mark a single notification as read
 */
export const markNotificationRead = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<MarkNotificationReadData, ThrowOnError>) => {
    return (options?.client ?? client).put<MarkNotificationReadResponse, MarkNotificationReadError, ThrowOnError>({
        ...options,
        url: '/users/notifications/{notificationId}/read'
    });
};

/**
 * The caller's full ownership graph (post-ADR-002).
 * On first onboarding the backend fans out a fleet of OBO-scoped queries
 * (Phonetool, AAA/RIP, Bindles, Taskei, CTI, Isengard, LDAP, Apollo,
 * Pipelines, SIM, Slack, Sage, Quip, Oncall, Kingpin) and persists the
 * consolidated graph keyed on `employeeId`. Subsequent calls return the
 * cached snapshot; pass `?refresh=true` to force a re-discovery
 * (rate-limited server-side to ~1 / 60s per employee).
 *
 */
export const getUserGraph = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<GetUserGraphData, ThrowOnError>) => {
    return (options?.client ?? client).get<GetUserGraphResponse, GetUserGraphError, ThrowOnError>({
        ...options,
        url: '/user-graph'
    });
};

/**
 * Live progress of a first-time discovery crawl.
 * Polled (or upgraded to a WS topic) by the onboarding "silent
 * discovery" loading screen so the user sees real progress instead
 * of a fake spinner. Each entry corresponds to one upstream
 * connector and reports its current state. When every connector is
 * terminal the client redirects to /.
 *
 */
export const getDiscoveryStatus = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).get<GetDiscoveryStatusResponse, GetDiscoveryStatusError, ThrowOnError>({
        ...options,
        url: '/user-graph/discovery-status'
    });
};

/**
 * List bound downstream services
 */
export const listConnections = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).get<ListConnectionsResponse, ListConnectionsError, ThrowOnError>({
        ...options,
        url: '/connections'
    });
};

/**
 * List the caller's memory items (paged)
 * Memory is keyed on `employeeId` alone (post-ADR-002). The server
 * enforces ownership; the frontend does NOT pass `employeeId` (that
 * comes from the Midway cookie).
 *
 */
export const listMemory = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<ListMemoryData, ThrowOnError>) => {
    return (options?.client ?? client).get<ListMemoryResponse, ListMemoryError, ThrowOnError>({
        ...options,
        url: '/memory'
    });
};

/**
 * Bind the caller's Slack identity
 * The Slack OAuth flow lands here with `code` + `state`. The server
 * exchanges the code, persists the team / user binding, and returns the
 * bound identity.
 *
 */
export const slackOauthCallback = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<SlackOauthCallbackData, ThrowOnError>) => {
    return (options?.client ?? client).post<SlackOauthCallbackResponse, SlackOauthCallbackError, ThrowOnError>({
        ...options,
        url: '/slack/oauth/callback'
    });
};

/**
 * List the caller's authored custom agents
 */
export const listCustomAgents = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).get<ListCustomAgentsResponse, ListCustomAgentsError, ThrowOnError>({
        ...options,
        url: '/customAgents'
    });
};

/**
 * Create a custom agent
 */
export const createCustomAgent = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<CreateCustomAgentData, ThrowOnError>) => {
    return (options?.client ?? client).post<CreateCustomAgentResponse, CreateCustomAgentError, ThrowOnError>({
        ...options,
        url: '/customAgents'
    });
};

/**
 * Fetch one custom agent
 */
export const getCustomAgent = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<GetCustomAgentData, ThrowOnError>) => {
    return (options?.client ?? client).get<GetCustomAgentResponse, GetCustomAgentError, ThrowOnError>({
        ...options,
        url: '/customAgents/{customAgentId}'
    });
};

/**
 * Replace a custom agent (bumps version hash, revokes consents)
 */
export const updateCustomAgent = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<UpdateCustomAgentData, ThrowOnError>) => {
    return (options?.client ?? client).put<UpdateCustomAgentResponse, UpdateCustomAgentError, ThrowOnError>({
        ...options,
        url: '/customAgents/{customAgentId}'
    });
};

/**
 * Delete a custom agent
 */
export const deleteCustomAgent = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<DeleteCustomAgentData, ThrowOnError>) => {
    return (options?.client ?? client).delete<DeleteCustomAgentResponse, DeleteCustomAgentError, ThrowOnError>({
        ...options,
        url: '/customAgents/{customAgentId}'
    });
};

/**
 * List the caller's workflows
 */
export const listWorkflows = <ThrowOnError extends boolean = false>(options?: OptionsLegacyParser<unknown, ThrowOnError>) => {
    return (options?.client ?? client).get<ListWorkflowsResponse, ListWorkflowsError, ThrowOnError>({
        ...options,
        url: '/workflows'
    });
};

/**
 * Create a workflow
 */
export const createWorkflow = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<CreateWorkflowData, ThrowOnError>) => {
    return (options?.client ?? client).post<CreateWorkflowResponse, CreateWorkflowError, ThrowOnError>({
        ...options,
        url: '/workflows'
    });
};

/**
 * Fetch one workflow
 */
export const getWorkflow = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<GetWorkflowData, ThrowOnError>) => {
    return (options?.client ?? client).get<GetWorkflowResponse, GetWorkflowError, ThrowOnError>({
        ...options,
        url: '/workflows/{workflowId}'
    });
};

/**
 * Replace a workflow
 */
export const updateWorkflow = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<UpdateWorkflowData, ThrowOnError>) => {
    return (options?.client ?? client).put<UpdateWorkflowResponse, UpdateWorkflowError, ThrowOnError>({
        ...options,
        url: '/workflows/{workflowId}'
    });
};

/**
 * Delete a workflow
 */
export const deleteWorkflow = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<DeleteWorkflowData, ThrowOnError>) => {
    return (options?.client ?? client).delete<DeleteWorkflowResponse, DeleteWorkflowError, ThrowOnError>({
        ...options,
        url: '/workflows/{workflowId}'
    });
};

/**
 * Dry-run a workflow against the caller's current consent state
 */
export const dryRunWorkflow = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<DryRunWorkflowData, ThrowOnError>) => {
    return (options?.client ?? client).post<DryRunWorkflowResponse, DryRunWorkflowError, ThrowOnError>({
        ...options,
        url: '/workflows/{workflowId}/dryRun'
    });
};

/**
 * Trigger a workflow run (manual trigger)
 */
export const runWorkflow = <ThrowOnError extends boolean = false>(options: OptionsLegacyParser<RunWorkflowData, ThrowOnError>) => {
    return (options?.client ?? client).post<RunWorkflowResponse, RunWorkflowError, ThrowOnError>({
        ...options,
        url: '/workflows/{workflowId}/run'
    });
};